import React, { useMemo, useState, Fragment } from 'react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { ChevronDownIcon, ChevronUpIcon, PlusIcon, SendIcon, BanIcon } from 'lucide-react';
import { formatCurrency } from './pharmacyUtils';
type DraftLine = {
  medicineId: string;
  quantity: string;
  unitCost: string;
};
const newLine = (): DraftLine => ({
  medicineId: '',
  quantity: '',
  unitCost: ''
});
export function PurchaseOrders() {
  const {
    suppliers,
    medicines,
    purchaseOrders,
    createPurchaseOrder,
    updatePurchaseOrder,
    cancelPurchaseOrder,
    currentUser
  } = useData();
  const [showForm, setShowForm] = useState(false);
  const [supplierId, setSupplierId] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([newLine()]);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const manager = currentUser?.role === 'Pharmacy Manager';
  const orders = useMemo(() => [...purchaseOrders].sort((a, b) => new Date(b.dateReceived || b.createdAt).getTime() - new Date(a.dateReceived || a.createdAt).getTime()), [purchaseOrders]);
  const updateLine = (index: number, key: keyof DraftLine, value: string) => setLines(current => current.map((line, itemIndex) => itemIndex === index ? {
    ...line,
    [key]: value
  } : line));
  const cancelOrder = async (id: string) => {
    const reason = window.prompt('Enter the mandatory cancellation reason. The purchase order will remain in the audit register.');
    if (!reason) return;
    setCancellingId(id);
    const result = await cancelPurchaseOrder(id, reason);
    setCancellingId(null);
    if (!result.ok) setError(result.error || 'Unable to cancel purchase order.');
  };
  const save = (status: 'Draft' | 'Pending') => {
    const supplier = suppliers.find(item => item.id === supplierId);
    const parsedLines = lines.filter(line => line.medicineId && Number(line.quantity) > 0 && Number(line.unitCost) >= 0).map(line => {
      const medicine = medicines.find(item => item.id === line.medicineId)!;
      return {
        medicineId: medicine.id,
        medicineName: medicine.name,
        quantityOrdered: Number(line.quantity),
        quantityReceived: 0,
        unitCost: Number(line.unitCost)
      };
    });
    if (!supplier || !parsedLines.length) return setError('Select a supplier and add at least one valid medicine line.');
    createPurchaseOrder({
      supplierId: supplier.id,
      supplierName: supplier.name,
      status,
      orderedAt: new Date().toISOString(),
      expectedDate: expectedDate || undefined,
      notes: notes || undefined,
      lines: parsedLines,
      createdBy: currentUser?.id || '',
      kind: 'Planning'
    });
    setShowForm(false);
    setSupplierId('');
    setExpectedDate('');
    setNotes('');
    setLines([newLine()]);
    setError('');
  };
  return <div className="space-y-5" data-id="element-4939">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-4940">
        <div data-id="element-4941">
          <h2 className="text-xl font-bold text-slate-950" data-id="element-4942">Purchase Orders</h2>
          <p className="mt-1 text-sm text-slate-500" data-id="element-4943">
            Planning orders and completed delivery receipts share one live
            procurement register.
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-teal-600 hover:bg-teal-700" data-id="element-4944">
          <PlusIcon className="mr-2 h-4 w-4" data-id="element-4945" /> New purchase order
        </Button>
      </div>
      {showForm && <section className="rounded-xl border border-teal-200 bg-teal-50/50 p-5" data-id="element-4946">
          <div className="mb-4 flex justify-between" data-id="element-4947">
            <div data-id="element-4948">
              <h3 className="font-bold text-slate-900" data-id="element-4949">New purchase order</h3>
              <p className="mt-1 text-xs text-slate-500" data-id="element-4950">
                Use Receive New Stock to turn delivered items into an audited
                receipt.
              </p>
            </div>
            <button type="button" onClick={() => setShowForm(false)} className="text-sm font-semibold text-slate-500 hover:text-slate-900" data-id="element-4951">
              Cancel
            </button>
          </div>
          {error && <p role="alert" className="mb-3 text-sm text-red-600" data-id="element-4952">
              {error}
            </p>}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-id="element-4953">
            <Select label="Supplier" value={supplierId} onChange={event => setSupplierId(event.target.value)} options={[{
          value: '',
          label: 'Select supplier'
        }, ...suppliers.filter(supplier => supplier.status === 'Active').map(supplier => ({
          value: supplier.id,
          label: supplier.name
        }))]} data-id="element-4954" />
            <Input label="Expected date" type="date" value={expectedDate} onChange={event => setExpectedDate(event.target.value)} data-id="element-4955" />
          </div>
          <div className="mt-4 overflow-x-auto" data-id="element-4956">
            <table className="min-w-[680px] w-full" data-id="element-4957">
              <thead data-id="element-4958">
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-4959">
                  <th className="pb-2" data-id="element-4960">Medicine</th>
                  <th className="pb-2" data-id="element-4961">Quantity</th>
                  <th className="pb-2" data-id="element-4962">Buying price</th>
                </tr>
              </thead>
              <tbody data-id="element-4963">
                {lines.map((line, index) => <tr key={index} data-id="element-4964">
                    <td className="min-w-64 pb-2 pr-3" data-id="element-4965">
                      <select aria-label="Medicine" value={line.medicineId} onChange={event => {
                  const medicine = medicines.find(item => item.id === event.target.value);
                  updateLine(index, 'medicineId', event.target.value);
                  if (!manager && medicine) updateLine(index, 'unitCost', String(medicine.buyingPrice));
                }} className="w-full rounded-md border border-slate-300 bg-white p-2 text-sm" data-id="element-4966">
                        <option value="" data-id="element-4967">Select medicine</option>
                        {medicines.map(medicine => <option key={medicine.id} value={medicine.id} data-id="element-4968">
                            {medicine.name}
                          </option>)}
                      </select>
                    </td>
                    <td className="pb-2 pr-3" data-id="element-4969">
                      <input aria-label="Quantity" type="number" min="1" value={line.quantity} onChange={event => updateLine(index, 'quantity', event.target.value)} className="w-28 rounded-md border border-slate-300 p-2 text-sm" data-id="element-4970" />
                    </td>
                    <td className="pb-2" data-id="element-4971">
                      <input aria-label="Buying price" type="number" min="0" step="0.01" value={line.unitCost} disabled={!manager} onChange={event => updateLine(index, 'unitCost', event.target.value)} className="w-32 rounded-md border border-slate-300 p-2 text-sm" data-id="element-4972" />
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <button type="button" onClick={() => setLines(current => [...current, newLine()])} className="mt-2 text-sm font-semibold text-teal-700 hover:text-teal-900" data-id="element-4973">
            + Add medicine line
          </button>
          <label className="mt-4 block text-sm font-medium text-slate-700" data-id="element-4974">
            Notes
            <textarea value={notes} onChange={event => setNotes(event.target.value)} rows={2} className="mt-1 block w-full rounded-md border border-slate-300 p-2 text-sm" data-id="element-4975" />
          </label>
          <div className="mt-4 flex justify-end gap-2" data-id="element-4976">
            <Button variant="outline" onClick={() => save('Draft')} data-id="element-4977">
              Save draft
            </Button>
            <Button onClick={() => save('Pending')} className="bg-teal-600 hover:bg-teal-700" data-id="element-4978">
              <SendIcon className="mr-2 h-4 w-4" data-id="element-4979" /> Submit order
            </Button>
          </div>
        </section>}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-4980">
        <div className="overflow-x-auto" data-id="element-4981">
          <table className="min-w-[1050px] w-full divide-y divide-slate-100" data-id="element-4982">
            <thead className="bg-slate-50" data-id="element-4983">
              <tr data-id="element-4984">
                {['Order / receipt', 'Supplier & invoice', 'Received', 'Total cost', 'Status', ''].map(heading => <th key={heading} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-4985">
                    {heading}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-4986">
              {orders.map(order => <Fragment key={order.id} data-id="element-4987">
                  <tr data-id="element-4988">
                    <td className="px-5 py-3.5" data-id="element-4989">
                      <p className="font-semibold text-slate-900" data-id="element-4990">
                        {order.orderNumber}
                      </p>
                      <p className="mt-1 text-xs text-slate-500" data-id="element-4991">
                        {order.kind === 'Receipt' ? 'Delivery receipt' : 'Procurement plan'}
                      </p>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-700" data-id="element-4992">
                      <p data-id="element-4993">{order.supplierName}</p>
                      <p className="mt-1 text-xs text-slate-500" data-id="element-4994">
                        {order.invoiceNumber ? `Invoice ${order.invoiceNumber}` : 'Invoice recorded on receipt'}
                      </p>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-4995">
                      {order.dateReceived ? <>
                          <p data-id="element-4996">
                            {new Date(order.dateReceived).toLocaleDateString()}
                          </p>
                          <p className="mt-1 text-xs text-slate-500" data-id="element-4997">
                            {order.receivedByName}
                          </p>
                        </> : order.expectedDate ? `Expected ${new Date(`${order.expectedDate}T00:00:00`).toLocaleDateString()}` : '—'}
                    </td>
                    <td className="px-5 py-3.5 text-sm font-medium text-slate-700" data-id="element-4998">
                      {formatCurrency(order.totalCost ?? order.lines.reduce((sum, line) => sum + line.quantityOrdered * line.unitCost, 0))}
                    </td>
                    <td className="px-5 py-3.5" data-id="element-4999">
                      <Status status={order.status} data-id="element-5000" />
                    </td>
                    <td className="px-5 py-3.5 text-right" data-id="element-5001">
                      <div className="flex justify-end gap-3" data-id="element-5002">
                        {order.status === 'Draft' && <button type="button" onClick={() => updatePurchaseOrder(order.id, {
                      status: 'Pending'
                    })} className="text-sm font-semibold text-teal-700 hover:text-teal-900" data-id="element-5003">
                            Submit
                          </button>}
                        {manager && order.kind !== 'Receipt' && !['Received', 'Cancelled'].includes(order.status) && <button type="button" disabled={cancellingId === order.id} onClick={() => cancelOrder(order.id)} className="inline-flex items-center text-sm font-semibold text-red-700 hover:text-red-900 disabled:opacity-50" data-id="element-5004">
                              <BanIcon className="mr-1 h-4 w-4" data-id="element-5005" />
                              {cancellingId === order.id ? 'Cancelling…' : 'Cancel'}
                            </button>}
                        <button type="button" onClick={() => setExpandedId(expandedId === order.id ? null : order.id)} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900" data-id="element-5006">
                          Items{' '}
                          {expandedId === order.id ? <ChevronUpIcon className="h-4 w-4" data-id="element-5007" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-5008" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedId === order.id && <tr className="bg-slate-50/60" data-id="element-5009">
                      <td colSpan={6} className="px-5 py-4" data-id="element-5010">
                        <table className="min-w-full text-sm" data-id="element-5011">
                          <thead className="text-left text-xs uppercase tracking-wide text-slate-500" data-id="element-5012">
                            <tr data-id="element-5013">
                              <th className="pb-2" data-id="element-5014">Medicine</th>
                              <th className="pb-2" data-id="element-5015">Batch</th>
                              <th className="pb-2" data-id="element-5016">Quantity</th>
                              <th className="pb-2" data-id="element-5017">Buying price</th>
                              <th className="pb-2" data-id="element-5018">Received</th>
                            </tr>
                          </thead>
                          <tbody data-id="element-5019">
                            {order.lines.map(line => <tr key={`${order.id}-${line.medicineId}-${line.batchNumber || ''}`} data-id="element-5020">
                                <td className="py-1.5 font-medium text-slate-800" data-id="element-5021">
                                  {line.medicineName}
                                </td>
                                <td className="py-1.5 text-slate-600" data-id="element-5022">
                                  {line.batchNumber || '—'}
                                </td>
                                <td className="py-1.5 text-slate-600" data-id="element-5023">
                                  {line.quantityOrdered}
                                </td>
                                <td className="py-1.5 text-slate-600" data-id="element-5024">
                                  {formatCurrency(line.buyingPrice ?? line.unitCost)}
                                </td>
                                <td className="py-1.5 text-slate-600" data-id="element-5025">
                                  {line.quantityReceived}
                                </td>
                              </tr>)}
                          </tbody>
                        </table>
                      </td>
                    </tr>}
                </Fragment>)}
              {!orders.length && <tr data-id="element-5026">
                  <td colSpan={6} className="px-5 py-14 text-center text-sm text-slate-500" data-id="element-5027">
                    No purchase orders or receipts yet.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>;
}
function Status({
  status
}: {
  status: string;
}) {
  const colors: Record<string, string> = {
    Draft: 'bg-slate-100 text-slate-700',
    Pending: 'bg-blue-50 text-blue-700',
    'Partially Received': 'bg-amber-50 text-amber-700',
    Received: 'bg-emerald-50 text-emerald-700',
    Cancelled: 'bg-red-50 text-red-700'
  };
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${colors[status] || 'bg-slate-100 text-slate-700'}`} data-id="element-5028">
      {status}
    </span>;
}